import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { navigateTo } from '@makemydeal/dr-activities-common';

import { dealProposalRouteMap, dealSummaryRouteMap } from '@makemydeal/dr-sales-checkin-store';
import type { DealSummaryWorkflowStatus } from '@makemydeal/dr-sales-checkin-types';
import { requestProposalSelectors } from '../store';

/**
 * Redirects the deal-summary workflow to the screen that matches the current workflow status.
 * Mounted once at the activity root so the redirect is not duplicated per screen.
 *
 * The host's full-page-scroll flag changes which router this follows, not whether it follows:
 * under it the activity renders inside the host's scroll page on a `MemoryRouter` the host owns
 * (never the browser's), so `history.replace` there moves only that memory history. Dispatching
 * `navigateTo` in that mode would move the browser off the scroll page instead. The resolved
 * route is still returned either way, for `Root`'s own `<Redirect>` fallback on first mount.
 */
export const useInitializeWorkflowNavigation = (requestQuoteRoute?: string): string | undefined => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname, search } = useLocation();
    const isFullScrollEnabled = useSelector(requestProposalSelectors.additionalSelectors.isFullPageScrollEnabled);
    const workflowStatus = useSelector(requestProposalSelectors.additionalSelectors.getWorkflowStatus);
    const isDealProposalEnabled = useSelector(requestProposalSelectors.additionalSelectors.isMultiScenarioOfferEnabled);
    const routeMap = isDealProposalEnabled ? dealProposalRouteMap : dealSummaryRouteMap;
    const targetRoute = routeMap[workflowStatus as DealSummaryWorkflowStatus];

    useEffect(() => {
        if (isFullScrollEnabled) {
            const target = targetRoute ?? requestQuoteRoute;

            if (target && target !== pathname) {
                history.replace({ pathname: target, search });
            }

            return;
        }

        if (targetRoute) {
            dispatch(navigateTo(targetRoute));
        }
    }, [targetRoute, dispatch, isFullScrollEnabled, history, pathname, search, requestQuoteRoute]);

    return targetRoute;
};
